import { useState } from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import type { MaintenanceRequest } from "../../../store/api/maintenanceApi";

interface MaintenanceTableProps {
	requests: MaintenanceRequest[];
	selectedRequests: string[];
	onRequestSelect: (requestId: string, selected: boolean) => void;
	onSelectAll: (selected: boolean) => void;
	onRequestClick: (request: MaintenanceRequest) => void;
	onEditRequest?: (request: MaintenanceRequest) => void;
	onDeleteRequest: (requestId: string) => void;
}

const MaintenanceTable: React.FC<MaintenanceTableProps> = ({
	requests,
	selectedRequests,
	onRequestSelect,
	onSelectAll,
	onRequestClick,
	onEditRequest,
	onDeleteRequest,
}) => {
	const [sortConfig, setSortConfig] = useState<{
		key: string;
		direction: "asc" | "desc";
	}>({ key: "requested_date", direction: "desc" });

	const handleSort = (key: string) => {
		setSortConfig((prev) => ({
			key,
			direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
		}));
	};

	const sortedRequests = [...requests].sort((a, b) => {
		const aValue: any = a[sortConfig.key as keyof MaintenanceRequest];
		const bValue: any = b[sortConfig.key as keyof MaintenanceRequest];

		if (sortConfig.direction === "asc") {
			return aValue > bValue ? 1 : -1;
		} else {
			return aValue < bValue ? 1 : -1;
		}
	});

	const normalizeStatus = (status: string): string => {
		// Handle enum format like "MaintenanceStatus.PENDING" or "PENDING"
		if (status.includes(".")) {
			return status.split(".")[1].toLowerCase();
		}
		return status.toLowerCase();
	};

	const normalizePriority = (priority: string): string => {
		// Handle enum format like "MaintenancePriority.MEDIUM" or "MEDIUM"
		if (priority.includes(".")) {
			return priority.split(".")[1].toLowerCase();
		}
		return priority.toLowerCase();
	};

	const formatStatus = (status: string): string => {
		const normalized = normalizeStatus(status);
		return normalized.replace("_", " ");
	};

	const formatPriority = (priority: string): string => {
		return normalizePriority(priority);
	};

	const getStatusBadge = (status: string) => {
		const normalized = normalizeStatus(status);
		const baseClasses = "px-2 py-1 text-xs font-medium rounded-full capitalize";
		switch (normalized) {
			case "completed":
				return `${baseClasses} bg-success/10 text-success`;
			case "in_progress":
				return `${baseClasses} bg-primary/10 text-primary`;
			case "pending":
				return `${baseClasses} bg-warning/10 text-warning`;
			case "cancelled":
				return `${baseClasses} bg-muted text-muted-foreground`;
			case "on_hold":
				return `${baseClasses} bg-muted/50 text-muted-foreground`;
			default:
				return `${baseClasses} bg-muted text-muted-foreground`;
		}
	};

	const getPriorityBadge = (priority: string) => {
		const normalized = normalizePriority(priority);
		const baseClasses = "px-2 py-1 text-xs font-medium rounded-full capitalize";
		switch (normalized) {
			case "urgent":
				return `${baseClasses} bg-error/10 text-error`;
			case "high":
				return `${baseClasses} bg-warning/10 text-warning`;
			case "medium":
				return `${baseClasses} bg-primary/10 text-primary`;
			case "low":
				return `${baseClasses} bg-muted text-muted-foreground`;
			default:
				return `${baseClasses} bg-muted text-muted-foreground`;
		}
	};

	const formatDate = (dateString?: string) => {
		if (!dateString) return "N/A";
		return new Date(dateString).toLocaleDateString("en-US", {
			month: "short",
			day: "numeric",
			year: "numeric",
		});
	};

	return (
		<div className="bg-card border border-border rounded-lg card-shadow overflow-hidden">
			{/* Table Header */}
			<div className="p-4 border-b border-border">
				<div className="flex items-center justify-between">
					<h3 className="font-semibold text-foreground">
						Maintenance Requests
					</h3>
					<p className="text-sm text-muted-foreground">
						{requests.length} request{requests.length !== 1 ? "s" : ""}
					</p>
				</div>
			</div>

			{/* Table with horizontal scroll on mobile */}
			<div className="overflow-x-auto -mx-6 sm:mx-0">
				<div className="inline-block min-w-full align-middle sm:px-0">
					<table className="min-w-full divide-y divide-border">
					<thead className="bg-muted/50 border-b border-border">
						<tr>
							<th className="w-12 p-4">
								<input
									type="checkbox"
									checked={
										requests.length > 0 &&
										selectedRequests.length === requests.length
									}
									onChange={(e) => onSelectAll(e.target.checked)}
									className="rounded border-border"
								/>
							</th>
							<th className="text-left p-4 font-medium text-foreground w-48">
								<button
									onClick={() => handleSort("title")}
									className="flex items-center space-x-1 hover:text-primary"
								>
									<span>Title</span>
									<Icon
										name={
											sortConfig.key === "title" &&
											sortConfig.direction === "desc"
												? "ChevronDown"
												: "ChevronUp"
										}
										size={16}
									/>
								</button>
							</th>
							<th className="text-left p-4 font-medium text-foreground w-40">
								Property
							</th>
							<th className="text-left p-4 font-medium text-foreground w-32">
								<button
									onClick={() => handleSort("priority")}
									className="flex items-center space-x-1 hover:text-primary"
								>
									<span>Priority</span>
									<Icon
										name={
											sortConfig.key === "priority" &&
											sortConfig.direction === "desc"
												? "ChevronDown"
												: "ChevronUp"
										}
										size={16}
									/>
								</button>
							</th>
							<th className="text-left p-4 font-medium text-foreground w-32">
								<button
									onClick={() => handleSort("status")}
									className="flex items-center space-x-1 hover:text-primary"
								>
									<span>Status</span>
									<Icon
										name={
											sortConfig.key === "status" &&
											sortConfig.direction === "desc"
												? "ChevronDown"
												: "ChevronUp"
										}
										size={16}
									/>
								</button>
							</th>
							<th className="text-left p-4 font-medium text-foreground w-32">
								<button
									onClick={() => handleSort("requested_date")}
									className="flex items-center space-x-1 hover:text-primary"
								>
									<span>Requested</span>
									<Icon
										name={
											sortConfig.key === "requested_date" &&
											sortConfig.direction === "desc"
												? "ChevronDown"
												: "ChevronUp"
										}
										size={16}
									/>
								</button>
							</th>
							<th className="text-left p-4 font-medium text-foreground w-40">
								Assigned To
							</th>
							<th className="text-right p-4 font-medium text-foreground w-24">
								Actions
							</th>
						</tr>
					</thead>
					<tbody>
						{sortedRequests.map((request) => (
							<tr
								key={request.id}
								className="border-b border-border hover:bg-muted/30 cursor-pointer smooth-transition"
								onClick={() => onRequestClick(request)}
							>
								<td className="p-4" onClick={(e) => e.stopPropagation()}>
									<input
										type="checkbox"
										checked={selectedRequests.includes(request.id)}
										onChange={(e) =>
											onRequestSelect(request.id, e.target.checked)
										}
										className="rounded border-border"
									/>
								</td>
								<td className="p-4">
									<div>
										<p className="font-medium text-foreground">
											{request.title}
										</p>
										{request.location && (
											<p className="text-xs text-muted-foreground">
												📍 {request.location}
											</p>
										)}
									</div>
								</td>
								<td className="p-4">
									<p className="text-sm text-foreground">
										{request.property_name || "N/A"}
									</p>
								</td>
								<td className="p-4">
									<span className={getPriorityBadge(request.priority)}>
										{formatPriority(request.priority)}
									</span>
								</td>
								<td className="p-4">
									<span className={getStatusBadge(request.status)}>
										{formatStatus(request.status)}
									</span>
								</td>
								<td className="p-4">
									<p className="text-sm text-foreground">
										{formatDate(request.requested_date)}
									</p>
								</td>
								<td className="p-4">
									<p className="text-sm text-foreground">
										{request.assigned_to_name || "Unassigned"}
									</p>
								</td>
								<td className="p-4" onClick={(e) => e.stopPropagation()}>
									<div className="flex items-center justify-end space-x-1">
										{onEditRequest && (
											<Button
												variant="ghost"
												size="icon"
												onClick={() => onEditRequest(request)}
												className="h-8 w-8 text-primary hover:text-primary"
												title="Edit Request"
											>
												<Icon name="Edit" size={16} />
											</Button>
										)}
										<Button
											variant="ghost"
											size="icon"
											onClick={() => onDeleteRequest(request.id)}
											className="h-8 w-8 text-destructive hover:text-destructive"
											title="Delete Request"
										>
											<Icon name="Trash2" size={16} />
										</Button>
									</div>
								</td>
							</tr>
						))}
					</tbody>
				</table>
				</div>
			</div>

			{/* Mobile Card Layout */}
			<div className="lg:hidden bg-card border-x-0 sm:border-x border-t border-b sm:border border-border rounded-none sm:rounded-lg overflow-hidden -mx-3 sm:mx-0">
				{sortedRequests.map((request) => (
					<div
						key={request.id}
						className="p-4 border-b border-border last:border-b-0 bg-card hover:bg-muted/30 cursor-pointer smooth-transition active:bg-muted/50"
						onClick={() => onRequestClick(request)}
					>
						<div className="flex items-start gap-3">
							<input
								type="checkbox"
								checked={selectedRequests.includes(request.id)}
								onChange={(e) => onRequestSelect(request.id, e.target.checked)}
								onClick={(e) => e.stopPropagation()}
								className="mt-1 w-4 h-4 rounded border-border text-primary focus:ring-primary flex-shrink-0"
							/>
							<div className="flex-1 min-w-0 space-y-2">
								{/* Title and Actions */}
								<div className="flex items-start justify-between gap-2">
									<div className="flex-1 min-w-0">
										<p className="font-semibold text-sm sm:text-base text-foreground leading-tight mb-1">
											{request.title}
										</p>
										<p className="text-xs sm:text-sm text-muted-foreground">
											{request.property_name || "N/A"}
										</p>
									</div>
									<div className="flex items-center gap-1 flex-shrink-0">
										{onEditRequest && (
											<Button
												variant="ghost"
												size="icon"
												onClick={(e) => {
													e.stopPropagation();
													onEditRequest(request);
												}}
												className="h-7 w-7 text-primary hover:text-primary"
											>
												<Icon name="Edit" size={14} />
											</Button>
										)}
										<Button
											variant="ghost"
											size="icon"
											onClick={(e) => {
												e.stopPropagation();
												onDeleteRequest(request.id);
											}}
											className="h-7 w-7 text-destructive hover:text-destructive"
										>
											<Icon name="Trash2" size={14} />
										</Button>
									</div>
								</div>

								{/* Location if available */}
								{request.location && (
									<p className="text-xs text-muted-foreground flex items-center gap-1">
										<Icon name="MapPin" size={12} />
										{request.location}
									</p>
								)}

								{/* Priority, Status, and Date */}
								<div className="flex items-center justify-between gap-2 flex-wrap">
									<div className="flex items-center gap-2 flex-wrap">
										<span className={getPriorityBadge(request.priority)}>
											{formatPriority(request.priority)}
										</span>
										<span className={getStatusBadge(request.status)}>
											{formatStatus(request.status)}
										</span>
									</div>
									<p className="text-xs text-muted-foreground whitespace-nowrap">
										{formatDate(request.requested_date)}
									</p>
								</div>

								{/* Assigned To */}
								{request.assigned_to_name && (
									<p className="text-xs text-muted-foreground flex items-center gap-1">
										<Icon name="User" size={12} />
										Assigned: {request.assigned_to_name}
									</p>
								)}
							</div>
						</div>
					</div>
				))}
			</div>
		</div>
	);
};

export default MaintenanceTable;
